import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import { Droplets, Leaf, Factory, HeartHandshake, MapPin, Sprout, Building2, PackageCheck, ShieldCheck, BadgeCheck } from "lucide-react";
import { ASSETS, VENTURES } from "@/data/site";
import { Reveal, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const STATS = [
  { value: 1, prefix: "₹", suffix: " Cr", label: "Project investment" },
  { value: 10, prefix: "", suffix: " T", label: "Daily capacity per machine" },
  { value: 16500, prefix: "", suffix: " T", label: "Yearly output by 2032" },
  { value: 16, prefix: "", suffix: "%", label: "Project IRR" },
];

function CountUp({ value, prefix, suffix, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });
  const [ready, setReady] = useState(false);
  const [started, setStarted] = useState(false);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    let timer;
    const arm = () => {
      timer = setTimeout(() => setReady(true), 1200);
    };
    if (document.readyState === "complete") {
      arm();
    } else {
      window.addEventListener("load", arm);
    }
    return () => {
      window.removeEventListener("load", arm);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!inView || !ready || started) return;
    setStarted(true);
    animate(0, value, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v).toLocaleString("en-IN")),
    });
  }, [inView, ready, started, value]);

  const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <span ref={ref} data-testid={`stat-counter-${key}`}>
      {prefix}{display}{suffix}
    </span>
  );
}

const FOUNDERS = [
  {
    name: "Mr. Anuj Agrawal",
    role: "Partner, M/S Ananya Ventures",
    note: "Leads production, sourcing and quality — the discipline behind every batch.",
  },
  {
    name: "Mrs. Prachi Agrawal",
    role: "Partner, M/S Ananya Ventures",
    note: "Drives sales, distribution and retailer relationships across the territory.",
  },
];

const CITIES = [
  { name: "Bhopal", demand: "High" },
  { name: "Narmadapuram", demand: "Good" },
  { name: "Itarsi", demand: "Good" },
  { name: "Harda", demand: "Moderate" },
  { name: "Betul", demand: "Moderate" },
];

const MILESTONES = [
  {
    when: "Jan 2026",
    title: "Operations commence",
    body: "M/S Ananya Ventures begins production on 01.01.2026 with a 4-machine snack line.",
  },
  {
    when: "May 2026",
    title: "MSME registered",
    body: "UDYAM registration UDYAM-MP-10-0165541 issued on 18.05.2026.",
  },
  {
    when: "In progress",
    title: "MPIDC industrial plot",
    body: "Booking completed with MPIDC — allotment process underway for the new plant.",
  },
  {
    when: "2026 – 2032",
    title: "Scale-up roadmap",
    body: "Growing from 4 to 10 machines and 4,800 to 16,500 tonnes of yearly output.",
  },
];

const VALUES = [
  {
    icon: Droplets,
    title: "Purity, Verified",
    body: "Every batch of Ananya Refresh water passes RO purification and mineral balancing before it earns the label.",
  },
  {
    icon: Leaf,
    title: "Honest Ingredients",
    body: "Our snacks are 100% vegetarian, made with quality ingredients and seasoned for the Indian palate.",
  },
  {
    icon: Factory,
    title: "Made in the Heartland",
    body: "Produced at our Bhopal unit on Hoshangabad Road — close to the communities and retailers we serve.",
  },
  {
    icon: HeartHandshake,
    title: "Partners Over Customers",
    body: "From the smallest kirana to super stockists, we grow only when our retail partners grow.",
  },
];

const demandTone = (d) =>
  d === "High" ? "bg-lime/15 text-lime border-lime/40" : d === "Good" ? "bg-sun/10 text-sun-bright border-sun/40" : "bg-white/5 text-slate-300 border-white/15";

export default function About() {
  return (
    <div className="pt-40">
      <section className="mx-auto max-w-6xl px-6">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_1fr] items-center">
          <Reveal>
            <Overline>Our Story</Overline>
            <h1 data-testid="about-story-heading" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Purity you can taste.
              <span className="block text-sun-bright">Refreshment you can trust.</span>
            </h1>
            <p className="mt-7 text-base leading-relaxed text-slate-300">
              Ananya Refresh is the consumer brand of M/S Ananya Ventures — founded by partners Anuj
              and Prachi Agrawal with a simple belief: pure, safe drinking water and bold, honest
              snacks should not be a luxury. Production began in January 2026, bottling RO-purified,
              mineral-rich water and crafting a snack range priced from ₹5 that has quickly become a
              daily habit.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-400">
              Every bottle and every pack carries the same promise printed on our label:
              Pure. Safe. Refreshing.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-col gap-6">
              <div className="rounded-3xl border border-lime/25 bg-lime/5 p-10 flex flex-col items-center text-center" data-testid="about-ventures-card">
                <span className="inline-block bg-white rounded-2xl px-8 py-5 shadow-xl">
                  <img src={ASSETS.logo} alt="Ananya Refresh" className="h-20 w-auto" />
                </span>
                <p className="mt-6 text-xs font-semibold uppercase tracking-[0.28em] text-lime">
                  An Ananya Ventures Enterprise
                </p>
                <div className="mt-6 flex flex-col gap-2.5 text-sm text-slate-300">
                  <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-lime" /> GST-registered · {VENTURES.gst}</span>
                  <span className="inline-flex items-center gap-2"><BadgeCheck size={15} className="text-lime" /> MSME · UDYAM-MP-10-0165541</span>
                </div>
              </div>
              <div className="spotlight-card rounded-3xl border border-white/10 p-3 rotate-1 transition-transform duration-700 hover:rotate-0">
                <img
                  data-testid="about-water-image"
                  src={ASSETS.waterBanner}
                  alt="Ananya Refresh packaged drinking water range"
                  className="rounded-2xl w-full object-cover"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="mt-28">
        <Marquee tone="lime" items={["Pure", "Safe", "Refreshing", "Healthy Hydration for a Better Life"]} />
      </div>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-stats">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="rounded-3xl border border-white/10 bg-navy-card p-8 text-center transition-colors duration-500 hover:border-sun/50">
                  <p className="font-display text-3xl sm:text-4xl font-extrabold text-sun-bright">
                    <CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} label={s.label} />
                  </p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>The People</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Founded by partners, run like family.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2" data-testid="about-founders">
          {FOUNDERS.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.12}>
              <div className="group h-full rounded-3xl border border-white/10 bg-navy-card p-10 transition-all duration-500 hover:border-lime/50 hover:-translate-y-1.5">
                <span className="inline-flex rounded-2xl bg-sun/10 p-4 text-sun-bright transition-colors duration-500 group-hover:bg-lime group-hover:text-navy">
                  <Sprout size={26} />
                </span>
                <h3 className="mt-7 font-display text-2xl font-bold">{f.name}</h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.22em] text-lime">{f.role}</p>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">{f.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>The Facility</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Two production lines. One quality standard.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3" data-testid="about-facility-highlight">
          <Reveal>
            <div className="h-full rounded-3xl border border-white/10 bg-navy-card p-8">
              <MapPin size={24} className="text-lime" />
              <h3 className="mt-5 font-display text-lg font-bold">Bhopal Production Unit</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Shop No. 04, Ground Floor, Jharneshwar Colony, Behind International School,
                Hoshangabad Road, Bhopal — with an MPIDC industrial plot booked for the next
                phase of expansion.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-white/10 bg-navy-card p-8">
              <PackageCheck size={24} className="text-lime" />
              <h3 className="mt-5 font-display text-lg font-bold">Snacks Line</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Potato chips, namkeen mixture, bhujia and sev in the pack sizes retailers actually
                ask for — ₹5, ₹10, ₹20 and ₹50.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="h-full rounded-3xl border border-white/10 bg-navy-card p-8">
              <Building2 size={24} className="text-lime" />
              <h3 className="mt-5 font-display text-lg font-bold">Spices Line</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Turmeric, red chilli, coriander, garam masala and chat masala — processed and packed
                for retail shelves and bulk buyers like hotels, dhabas and caterers.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>Where We Serve</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Five cities. Surveyed, stocked, selling.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
            Our launch market was mapped shop by shop — 9 wholesalers and 11 hotels and restaurants
            engaged across the region before the first case ever shipped.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="about-cities">
          {CITIES.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.07}>
              <div className="rounded-3xl border border-white/10 bg-navy-card p-6 text-center transition-all duration-500 hover:-translate-y-1.5 hover:border-lime/50">
                <p className="font-display text-lg font-bold">{c.name}</p>
                <span className={`mt-3 inline-block rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${demandTone(c.demand)}`}>
                  {c.demand} demand
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>The Road So Far</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Milestones, not promises.
          </h2>
        </Reveal>
        <div className="mt-14 relative" data-testid="about-milestones">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-white/10 hidden sm:block" />
          <div className="flex flex-col gap-10">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.08}>
                <div className="flex gap-6 sm:gap-10 items-start">
                  <div className="hidden sm:flex flex-col items-center">
                    <span className="h-10 w-10 rounded-full border border-lime/50 bg-navy-card flex items-center justify-center font-display text-xs font-extrabold text-lime">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-navy-card p-7 grow transition-colors duration-500 hover:border-sun/50">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sun-bright">{m.when}</p>
                    <h3 className="mt-2 font-display text-lg font-bold">{m.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>What We Stand For</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Four commitments behind every pack.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div className="group flex gap-6 rounded-3xl border border-white/10 bg-navy-card p-8 transition-all duration-500 hover:border-sun/50">
                <span className="font-display text-4xl font-extrabold text-stroke-lime shrink-0">
                  0{i + 1}
                </span>
                <div>
                  <div className="flex items-center gap-3">
                    <v.icon size={20} className="text-sun-bright" />
                    <h3 className="font-display text-lg font-bold">{v.title}</h3>
                  </div>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{v.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <div
            data-testid="about-range-showcase"
            className="rounded-[2.5rem] border border-white/10 bg-navy-card overflow-hidden grid lg:grid-cols-2"
          >
            <div className="p-10 sm:p-14 flex flex-col justify-center">
              <Overline>The Range</Overline>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                One brand. Twelve ways to refresh.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                From Masala Blast to Alu Bhujiya, from 1L family bottles to 200ml travel packs — the
                Ananya Refresh range is designed so every shelf in your shop earns its place.
              </p>
            </div>
            <div className="relative min-h-[320px] bg-white">
              <img
                data-testid="about-snack-range"
                src={ASSETS.snackGrid}
                alt="Ananya Refresh complete snack range"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
